import { useI18n } from '@/i18n'
import { CaretDown, MagnifyingGlass } from '@/shared/icons/phosphor'
import {
  AsYouType,
  type CountryCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'
import mobileExamples from 'libphonenumber-js/mobile/examples'
import {
  type Component,
  For,
  Show,
  createMemo,
  createSignal,
  createUniqueId,
  onCleanup,
  onMount,
} from 'solid-js'
import { FIELD_MESSAGE_METRICS, FIELD_MESSAGE_SLOT, FieldError } from '../../primitives/FieldError'
import { type Country, countries, defaultCountryCode } from './countries'

export interface PhoneInputProps {
  value?: string
  onChange: (e164: string) => void
  placeholder?: string
  label?: string | undefined
  disabled?: boolean
  error?: string
  hint?: string | undefined
  reserveMessageLine?: boolean
  class?: string
  'aria-labelledby'?: string | undefined
  'aria-describedby'?: string | undefined
  'aria-required'?: boolean | undefined
  inputRef?: (element: HTMLInputElement) => void
}

function matchInternational(value: string): { country: Country; local: string } | null {
  const parsed = parsePhoneNumberFromString(value)
  if (parsed?.country) {
    const match = countries.find((c) => c.code === parsed.country)
    if (match) {
      return { country: match, local: parsed.nationalNumber }
    }
  }

  const digits = value.slice(1)
  for (let len = 4; len >= 1; len--) {
    const prefix = `+${digits.slice(0, len)}`
    const match = countries.find((c) => c.dial === prefix)
    if (match) {
      return { country: match, local: digits.slice(len) }
    }
  }
  return null
}

export function parseE164(value: string): { country: Country; local: string } {
  const fallback = countries.find((c) => c.code === defaultCountryCode)!
  if (!value.startsWith('+')) {
    return { country: fallback, local: value }
  }
  return matchInternational(value) ?? { country: fallback, local: value.slice(1) }
}

function withoutCallingCode(international: string, callingCode: string): string {
  const prefix = `+${callingCode}`
  return international.startsWith(prefix)
    ? international.slice(prefix.length).trim()
    : international
}

function groupIncomplete(country: Country, local: string): string {
  const digits = local.replace(/\D/g, '')
  if (digits.startsWith('0') || digits !== local.trim()) {
    return new AsYouType(country.code as CountryCode).input(local)
  }
  const callingCode = country.dial.replace(/\D/g, '')
  return withoutCallingCode(new AsYouType().input(`+${callingCode}${digits}`), callingCode)
}

export function examplePlaceholder(country: Country): string {
  const example = getExampleNumber(country.code as CountryCode, mobileExamples)
  return example
    ? withoutCallingCode(example.formatInternational(), example.countryCallingCode)
    : ''
}

export function composeE164(country: Country, local: string): string {
  const cleaned = local.replace(/\D/g, '')
  if (!cleaned) {
    return ''
  }
  const dialDigits = country.dial.replace(/\D/g, '')
  if (cleaned === dialDigits) {
    return ''
  }
  const parsed = parsePhoneNumberFromString(cleaned, country.code as CountryCode)
  if (parsed?.isValid()) {
    return parsed.number
  }
  return country.dial + cleaned
}

export const PhoneInput: Component<PhoneInputProps> = (props) => {
  const { t } = useI18n()
  const errorId = createUniqueId()
  const hintId = createUniqueId()
  const numberId = createUniqueId()
  const showsHint = () => Boolean(props.hint) && !props.error
  const [open, setOpen] = createSignal(false)
  const [search, setSearch] = createSignal('')
  let containerRef: HTMLDivElement | undefined
  let searchInputRef: HTMLInputElement | undefined
  let numberInputRef: HTMLInputElement | undefined

  const [pickedCountry, setPickedCountry] = createSignal<Country | null>(null)
  const [typedPrefix, setTypedPrefix] = createSignal<{ shown: string; value: string } | null>(null)
  const parsed = createMemo(() => parseE164(props.value ?? ''))
  const selectedCountry = createMemo(() => {
    const picked = pickedCountry()
    if (picked && (!(props.value ?? '').startsWith('+') || parsed().country.dial === picked.dial)) {
      return picked
    }
    return parsed().country
  })
  const localNumber = () => parsed().local
  const displayNumber = () => {
    const prefix = typedPrefix()
    if (prefix && prefix.value === (props.value ?? '')) {
      return prefix.shown
    }
    const local = localNumber()
    if (!local) {
      return ''
    }
    const value = props.value ?? ''
    if (value.startsWith('+')) {
      const full = parsePhoneNumberFromString(value)
      if (full?.isValid()) {
        return withoutCallingCode(full.formatInternational(), full.countryCallingCode)
      }
    }
    return groupIncomplete(selectedCountry(), local)
  }

  const filtered = createMemo(() => {
    const q = search().toLowerCase()
    if (!q) {
      return countries
    }
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q)
    )
  })

  const describedBy = () =>
    [props['aria-describedby'], props.error ? errorId : null, showsHint() ? hintId : null]
      .filter(Boolean)
      .join(' ') || undefined

  const errorLine = () => (
    <Show
      when={props.error}
      fallback={
        <Show when={showsHint()}>
          <p
            id={hintId}
            data-field-hint
            class={`${FIELD_MESSAGE_METRICS} text-[color:var(--color-text-muted)]`}
          >
            {props.hint}
          </p>
        </Show>
      }
    >
      <FieldError id={errorId}>{props.error}</FieldError>
    </Show>
  )

  const emit = (country: Country, local: string) => {
    props.onChange(composeE164(country, local))
  }

  const handleInternationalInput = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    const international = `+${digits}`
    const match = digits ? matchInternational(international) : null
    if (!match) {
      setTypedPrefix({ shown: raw.trim(), value: digits ? international : '' })
      props.onChange(digits ? international : '')
      return
    }
    setTypedPrefix(null)
    setPickedCountry(match.country)
    emit(match.country, match.local)
  }

  const handleNumberInput = (e: InputEvent) => {
    const input = e.currentTarget as HTMLInputElement
    if (input.value.trimStart().startsWith('+')) {
      handleInternationalInput(input.value)
    } else {
      setTypedPrefix(null)
      emit(selectedCountry(), input.value)
    }
    const shown = displayNumber()
    if (input.value !== shown) {
      input.value = shown
    }
  }

  const handleBlur = () => {
    const current = props.value ?? ''
    if (!current) {
      return
    }
    const normalized = toE164(current)
    if (normalized && normalized !== current) {
      props.onChange(normalized)
    }
  }

  const selectCountry = (country: Country) => {
    setPickedCountry(country)
    setOpen(false)
    setSearch('')
    emit(country, localNumber())
    numberInputRef?.focus()
  }

  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef && !containerRef.contains(e.target as Node)) {
      setOpen(false)
      setSearch('')
    }
  }

  onMount(() => document.addEventListener('mousedown', handleClickOutside))
  onCleanup(() => document.removeEventListener('mousedown', handleClickOutside))

  return (
    <div ref={containerRef} class={`relative ${props.class ?? ''}`}>
      <div class="flex flex-col gap-1.5">
        <Show when={props.label}>
          <label
            for={numberId}
            class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]"
          >
            {props.label}
          </label>
        </Show>
        <div class="relative">
          <div
            data-phone-field
            class={`ds-field flex h-10 items-stretch rounded-[var(--radius-12)] border font-[family-name:var(--font-sans)] transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)] ${
              props.disabled
                ? 'cursor-not-allowed border-[var(--color-field-border)] bg-[var(--color-background)] opacity-50'
                : props.error
                  ? 'border-[var(--color-error)] bg-[var(--color-surface)] has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-[var(--color-error-600)]'
                  : 'border-[var(--color-field-border)] bg-[var(--color-surface)] hover:border-[var(--color-field-hover)] focus-within:border-[var(--color-primary-400)] focus-within:shadow-[var(--focus-ring)]'
            }`}
          >
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => {
                const next = !open()
                setOpen(next)
                setSearch('')
                if (next) {
                  searchInputRef?.focus()
                }
              }}
              class="flex items-center gap-1.5 px-3 border-r border-[var(--color-border)] text-[length:var(--text-sm)] text-[var(--color-text)] hover:bg-[var(--color-background)] transition-colors rounded-l-[var(--radius-12)] shrink-0 disabled:cursor-not-allowed"
            >
              <span class="text-[length:var(--text-base)] leading-none">
                {selectedCountry().flag}
              </span>
              <span
                data-phone-dial
                class={`text-[length:var(--text-sm)] tabular-nums ${
                  props.value
                    ? 'text-[color:var(--color-text)]'
                    : 'text-[color:var(--color-text-muted)]'
                }`}
              >
                {selectedCountry().dial}
              </span>
              <CaretDown class="w-3 h-3 text-[var(--color-text-subtle)]" />
            </button>

            <input
              ref={(element) => {
                numberInputRef = element
                props.inputRef?.(element)
              }}
              id={numberId}
              type="tel"
              inputMode="tel"
              aria-labelledby={props['aria-labelledby']}
              aria-describedby={describedBy()}
              aria-required={props['aria-required']}
              aria-invalid={props.error ? true : undefined}
              value={displayNumber()}
              onInput={handleNumberInput}
              onBlur={handleBlur}
              placeholder={props.placeholder ?? examplePlaceholder(selectedCountry())}
              disabled={props.disabled}
              class="flex-1 min-w-0 px-3.5 text-[length:var(--text-base)] text-[color:var(--color-text)] placeholder:text-[color:var(--color-text-subtle)] bg-transparent focus:outline-none rounded-r-[var(--radius-12)] disabled:cursor-not-allowed"
            />
          </div>
          <Show when={open()}>
            <div class="absolute top-full z-50 mt-1 w-full max-h-60 overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-float)]">
              <div class="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)]">
                <MagnifyingGlass class="w-3.5 h-3.5 text-[var(--color-text-subtle)] shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search()}
                  onInput={(e) => setSearch(e.currentTarget.value)}
                  placeholder={t('designSystem.phoneInput.searchCountry')}
                  class="flex-1 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] bg-transparent focus:outline-none"
                />
              </div>

              <div class="overflow-y-auto max-h-48">
                <For
                  each={filtered()}
                  fallback={
                    <div class="px-3 py-4 text-center text-xs text-[var(--color-text-muted)]">
                      {t('designSystem.phoneInput.noCountriesFound')}
                    </div>
                  }
                >
                  {(country) => (
                    <button
                      type="button"
                      onClick={() => selectCountry(country)}
                      class={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-[var(--color-neutral-100)] transition-colors ${
                        country.code === selectedCountry().code
                          ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-700)]'
                          : 'text-[var(--color-text)]'
                      }`}
                    >
                      <span class="text-base leading-none">{country.flag}</span>
                      <span class="flex-1 truncate">{country.name}</span>
                      <span class="text-xs text-[var(--color-text-muted)] tabular-nums shrink-0">
                        {country.dial}
                      </span>
                    </button>
                  )}
                </For>
              </div>
            </div>
          </Show>
        </div>

        <Show when={props.reserveMessageLine} fallback={errorLine()}>
          <div data-field-message class={FIELD_MESSAGE_SLOT}>
            {errorLine()}
          </div>
        </Show>
      </div>
    </div>
  )
}

export function isValidE164(value: string): boolean {
  return parsePhoneNumberFromString(value)?.isValid() ?? false
}

export function toE164(value: string): string {
  const { country, local } = parseE164(value)
  const composed = composeE164(country, local)
  if (!composed || isValidE164(composed)) {
    return composed
  }

  const dialDigits = country.dial.replace(/\D/g, '')
  const cleaned = local.replace(/\D/g, '')
  if (cleaned.startsWith(dialDigits)) {
    const retry = composeE164(country, cleaned.slice(dialDigits.length))
    if (retry && isValidE164(retry)) {
      return retry
    }
  }
  return composed
}
